import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Sparkles, Users, Search, Wand2 } from "lucide-react";

const TIER_COLORS = {
  A: "bg-purple-50 border-purple-200 text-purple-700",
  B: "bg-blue-50 border-blue-200 text-blue-700",
  C: "bg-emerald-50 border-emerald-200 text-emerald-700",
  D: "bg-amber-50 border-amber-200 text-amber-800",
};

const fmtINR = (n) => (n ? "₹" + Number(n).toLocaleString("en-IN") : "—");

export default function FestivalTiers() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [showSuggestDialog, setShowSuggestDialog] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [overwrite, setOverwrite] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/festival-tiers/overview");
      setOverview(data);
    } catch (e) {
      toast.error("Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const setTier = async (empId, newTier) => {
    try {
      await api.patch(`/employees/${empId}/festival-tier`, { festival_tier: newTier });
      toast.success("Tier updated");
      refresh();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const previewSuggestions = async () => {
    try {
      const { data } = await api.post("/employees/bulk-suggest-tiers", { apply: false, overwrite_existing: overwrite });
      setSuggestions(data.suggestions || []);
      setShowSuggestDialog(true);
    } catch (e) {
      toast.error("Failed");
    }
  };

  const applySuggestions = async () => {
    try {
      const { data } = await api.post("/employees/bulk-suggest-tiers", { apply: true, overwrite_existing: overwrite });
      toast.success(`Applied to ${data.applied} employee(s)`);
      setShowSuggestDialog(false);
      refresh();
    } catch (e) {
      toast.error("Apply failed");
    }
  };

  if (loading || !overview) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const allEmployees = ["A", "B", "C", "D"].flatMap((t) => overview.tiers[t].employees);
  const visible = allEmployees.filter((e) => {
    if (tierFilter !== "all" && e._effective_tier !== tierFilter) return false;
    if (search && !(e.name || "").toLowerCase().includes(search.toLowerCase()) &&
        !(e.punch_id || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="festival-tiers">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/festivals")}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Users className="h-3 w-3" /> HR Tools
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Festival Tier Management
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Auto-suggested tiers from role + CTC. HR can override any employee's tier manually.
          </p>
        </div>
        <Button onClick={previewSuggestions} className="brand-bg brand-bg-hover text-white" size="sm" data-testid="auto-suggest-btn">
          <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Auto-Suggest Tiers
        </Button>
      </div>

      {/* Tier stats */}
      <div className="grid grid-cols-4 gap-3">
        {["A", "B", "C", "D"].map((t) => (
          <div key={t} className={`border rounded-lg p-3 ${TIER_COLORS[t]}`} data-testid={`tier-stat-${t}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold">Tier {t}</div>
            <div className="text-[10px] opacity-75 mt-0.5">{overview.tiers[t].label.split("—")[1]?.trim()}</div>
            <div className="text-2xl font-bold mt-1.5" style={{ fontFamily: "Chivo" }}>{overview.tiers[t].headcount}</div>
            <div className="text-[10px] opacity-75">employees</div>
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs flex items-center gap-2" data-testid="unset-warning">
        <Sparkles className="h-3.5 w-3.5 text-amber-700" />
        <span>
          <b>{overview.unset_count}</b> employee(s) have no tier set — using auto-suggested tier.
          Click <b>Auto-Suggest Tiers</b> to lock them in.
        </span>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            placeholder="Search by name or punch ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-7 h-8 text-sm"
            data-testid="search-input"
          />
        </div>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="w-40 h-8 text-sm" data-testid="tier-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="A">Tier A only</SelectItem>
            <SelectItem value="B">Tier B only</SelectItem>
            <SelectItem value="C">Tier C only</SelectItem>
            <SelectItem value="D">Tier D only</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">Showing {visible.length} of {overview.total_employees}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2 font-semibold">Punch ID</th>
              <th className="px-3 py-2 font-semibold">Name</th>
              <th className="px-3 py-2 font-semibold">Role</th>
              <th className="px-3 py-2 font-semibold text-right">CTC</th>
              <th className="px-3 py-2 font-semibold">Current Tier</th>
              <th className="px-3 py-2 font-semibold">Suggested</th>
              <th className="px-3 py-2 font-semibold">Override To</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((e) => {
              const mismatch = e.festival_tier && e.festival_tier !== e._suggested_tier;
              return (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`tier-row-${e.id}`}>
                  <td className="px-3 py-1.5 font-mono text-[10px]">{e.punch_id || "—"}</td>
                  <td className="px-3 py-1.5 font-semibold">{e.name}</td>
                  <td className="px-3 py-1.5 text-slate-600">{e.role || "employee"}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtINR(e.ctc)}</td>
                  <td className="px-3 py-1.5">
                    {e.festival_tier ? (
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${TIER_COLORS[e.festival_tier]}`}>
                        Tier {e.festival_tier}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic text-[10px]">Not set (using auto)</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${TIER_COLORS[e._suggested_tier]} opacity-70`}>
                      {e._suggested_tier}
                    </span>
                    {mismatch && <span className="ml-1 text-[9px] text-amber-700 font-bold">⚠ mismatch</span>}
                  </td>
                  <td className="px-3 py-1.5">
                    <Select
                      value={e.festival_tier || ""}
                      onValueChange={(v) => setTier(e.id, v)}
                    >
                      <SelectTrigger className="h-7 text-xs w-24" data-testid={`override-select-${e.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">Tier A</SelectItem>
                        <SelectItem value="B">Tier B</SelectItem>
                        <SelectItem value="C">Tier C</SelectItem>
                        <SelectItem value="D">Tier D</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Suggestion dialog */}
      <Dialog open={showSuggestDialog} onOpenChange={setShowSuggestDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="suggest-dialog">
          <DialogHeader>
            <DialogTitle>Auto-Suggest Tier Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                id="overwrite-cb"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                data-testid="overwrite-cb"
              />
              <label htmlFor="overwrite-cb">Overwrite existing manual tier assignments</label>
              <Button variant="link" size="sm" onClick={previewSuggestions} className="ml-auto">Refresh Preview</Button>
            </div>
            {suggestions.length === 0 ? (
              <div className="text-sm text-slate-500 italic py-4 text-center">
                No changes needed. All employees have appropriate tiers.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <th className="px-2 py-1.5 font-semibold">Name</th>
                    <th className="px-2 py-1.5 font-semibold">Current</th>
                    <th className="px-2 py-1.5 font-semibold">→ Suggested</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.slice(0, 50).map((s) => (
                    <tr key={s.employee_id} className="border-t border-slate-100">
                      <td className="px-2 py-1">{s.name}</td>
                      <td className="px-2 py-1">{s.current_tier ? `Tier ${s.current_tier}` : <i className="text-slate-400">Unset</i>}</td>
                      <td className="px-2 py-1 font-bold">Tier {s.suggested_tier}</td>
                    </tr>
                  ))}
                  {suggestions.length > 50 && (
                    <tr><td colSpan="3" className="px-2 py-1 text-center text-[10px] text-slate-500">…and {suggestions.length - 50} more</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuggestDialog(false)}>Cancel</Button>
            <Button onClick={applySuggestions} disabled={suggestions.length === 0} className="brand-bg brand-bg-hover text-white" data-testid="apply-suggestions-btn">
              Apply to {suggestions.length} employee(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
